import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getToken } from 'selectors/auth'
import env from 'config/env'
import { logout } from 'actions/auth'
import GraphQLError from 'lib/graphql-error'

const MAX_RETRIES = 3
const RETRY_DELAY_MS = 1000

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Axios client configured with interceptors
 * - Automatically adds Authorization header with JWT token
 * - Handles 401 errors by redirecting to login
 * - Retries on 503 errors (e.g. cold start)
 */

const apiClient: AxiosInstance = axios.create({
  baseURL: env.API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * Request interceptor
 * Add Authorization header with token to every request
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken()

    if (token && config.headers) {
      config.headers.Authorization = token
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

/**
 * Response interceptor
 * - Check for GraphQL errors in response body (status 200 with errors array)
 * - Handle 401 errors by redirecting to login page
 */
apiClient.interceptors.response.use(
  (response) => {
    const errors = response.data?.errors
    if (errors?.length) {
      return Promise.reject(new GraphQLError(errors))
    }
    return response
  },
  async (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      logout()
      return Promise.reject(error)
    }

    // Retry on 503 (Service Unavailable) - typically cold start
    if (error.response?.status === 503) {
      const config = error.config
      config.__retryCount = config.__retryCount || 0

      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount++
        const delayMs = RETRY_DELAY_MS * config.__retryCount
        // eslint-disable-next-line no-console
        console.log('Retrying request after 503', {
          attempt: config.__retryCount,
          url: config.url,
          delayMs,
        })
        await delay(delayMs)
        return apiClient(config)
      }
    }

    const gqlErrors = error.response?.data?.errors
    if (gqlErrors?.length) {
      return Promise.reject(new GraphQLError(gqlErrors))
    }
    return Promise.reject(error)
  },
)

export default apiClient
